/**
 * @file Context-aware "Back to …" link for detail pages.
 *
 * Uses the router's previousRoute() so the link goes back to where the user
 * actually came from: a person opened from Onboarding gets "Back to
 * Onboarding", a device opened from a person page gets "Back to person"
 * (that exact person), etc. On a fresh load / deep link there is no
 * previous route — falls back to the page's default list.
 *
 * Deliberately an href to the previous route (a normal forward navigation),
 * not history.back(): the label and the destination can never disagree,
 * even after the user mixes in browser back/forward.
 */

import { el } from '../utils.js';
import { Icons } from '../icons.js';
import { getRoute, previousRoute } from '../router.js';
import { listNeighbours } from './list-memory.js';

const SECTION_LABELS = {
  'me':           'Your gear',
  'people':       'People',
  'onboarding':   'Onboarding',
  'offboarding':  'Offboarding',
  'devices':      'Devices',
  'reconcile':    'Reconcile',
  'pending':      'Pending Abra',
  'data-quality': 'Data quality',
  'audit-log':    'Audit log',
  'settings':     'Settings',
};

// Sections whose `/:id` form is an ALIAS that main.js redirects to
// /people/:id — there is no such detail page any more. Linking back to the
// alias would bounce the user straight to the page they're leaving, so we
// always link to the LIST for these. (The router also keeps redirects out of
// prevRoute; this is the belt to that braces, e.g. for a deep link.)
const ALIAS_DETAIL_SECTIONS = new Set(['onboarding', 'offboarding']);

export function renderBackLink({ fallbackHref, fallbackLabel }) {
  let href  = fallbackHref;
  let label = fallbackLabel;

  const prev = previousRoute();
  const cur  = getRoute();
  // Walking a list with Prev / Next: the previous route is a sibling detail
  // page, but "back" still means the list those siblings came from.
  const list = prev?.id && prev.section === cur.section ? listNeighbours(cur.id) : null;
  if (list) {
    href = list.href;
    label = `Back to ${list.label}`;
  } else if (prev && prev.section && SECTION_LABELS[prev.section] && prev.raw !== cur.raw) {
    const isAlias = prev.id && ALIAS_DETAIL_SECTIONS.has(prev.section);
    href = isAlias ? `#/${prev.section}` : prev.raw;
    if (isAlias)                                    label = `Back to ${SECTION_LABELS[prev.section]}`;
    else if (prev.id && prev.section === 'people')   label = 'Back to person';
    else if (prev.id && prev.section === 'devices')  label = 'Back to device';
    else                                            label = `Back to ${SECTION_LABELS[prev.section]}`;
  }

  return el('div', { class: 'back-link' },
    el('a', { href, class: 'tab', style: 'display:inline-flex;align-items:center;gap:6px' },
      Icons.arrow_left(), label),
  );
}
